import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useState } from 'react';
import { AiOutlineRight } from 'react-icons/ai';

const Route2LabelMap = (route: string, isSpanish: boolean) => {
  if (route == '/') {
    return isSpanish ? 'Volver' : 'Back';
  }

  if (route === '/terms-and-conditions') {
    return isSpanish ? 'Términos y condiciones' : 'Terms and conditions';
  }

  return route.replace('/', '');
};

type BreadCrumbsProps = {
  currentLabel: string;
  subPathsToOmit?: number[];
};

function CombineAccumulatively(segments: string[]) {
  /*
  when segments = ['1','2','3']
  returns ['1','1/2','1/2/3']
  */
  const links = segments.reduce((acc: string[], cur, curIndex) => {
    const last = curIndex > 1 ? acc[curIndex - 1] : '';
    const newPath = last + '/' + cur;
    acc.push(newPath);
    return acc;
  }, []);
  return links;
}

const BreadCrumbs = ({
  currentLabel,
  subPathsToOmit,
}: BreadCrumbsProps): ReactElement | null => {
  const router = useRouter();
  const language = router.locale as SupportedLanguages;
  const isSpanish = language === 'es';

  const [crumbs, setCrumbs] = useState<
    {
      link: string;
      route: string;
      label: string;
    }[]
  >([]);

  useEffect(() => {
    const segmentsPath = router.asPath.split('/');
    const segmentsRoute = router.route.split('/');
    const crumbLinks = CombineAccumulatively(segmentsPath);
    const crumbLabels = CombineAccumulatively(segmentsRoute);

    const crumbs = crumbLinks.map((link: string, index: number) => {
      const route = crumbLabels[index];
      const crumb = {
        link: link,
        route: route,
        label:
          index == segmentsPath.length - 1
            ? currentLabel
            : segmentsPath[index]
            ? segmentsPath[index]
            : Route2LabelMap(route, isSpanish),
      };
      return crumb;
    });
    setCrumbs(crumbs);
  }, [currentLabel, router, router.route]);

  if (router.route == '/') {
    return null;
  }

  return (
    <div className="w-full flex gap-1 pt-[4.125rem] pl-4 pb-2">
      {crumbs
        .filter((c, i) => (subPathsToOmit ? !subPathsToOmit.includes(i) : true))
        .map((c, i, arr) => {
          return (
            <div
              className="flex items-center gap-1 text-sm md:text-base"
              key={i}
            >
              {i > 0 ? '/' : null}
              <div
                className={
                  (i == arr.length - 1 ? 'text-copper' : '') +
                  ' py-1 rounded-xl flex items-center gap-2'
                }
              >
                <Link href={c.link} className="flex items-center gap-1">
                  {i === 0 && (
                    <Image
                      src={'/left-arrow-circle-button.svg'}
                      height={24}
                      width={24}
                      alt="pin"
                      className="text-[#4B4B4B]"
                    />
                  )}
                  {c.label}
                </Link>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default BreadCrumbs;
